import moment from 'moment';
import { t } from '../../utils/i18n';
import { handleEventRoomNullUI, handleEventRoomUI, runRescheduleFlow } from "../../modules/actions/appointmentConfirmedActions";
import { getProviderFullName } from "../../modules/actions/providerActions";
import { params, updateUrlWithEventId } from "../../modules/events/movePages";
import { navigate } from "../../modules/events/movePages";
import { SCREEN } from "../../utils/constants";
import { EVENT_ID_PARAM, DROPOFF_RESUME_PARAM } from "../../utils/constants";
import { procedureDisplayName } from '../../utils/procedureDisplay';
import { isPhoneBooking, isRemoteBooking } from '../../utils/deliveryMode';
import { loadAppointmentCancel } from "./appointmentCancel";
import { appointmentCancelListeners } from "../../modules/events/appointmentCancelListeners";
import { appointmentSummary, eventPayload, eventRoom, form, locations, primaryColor, setEventPayload } from "../../utils/variables";
import { toastError } from "./error";
import { getEvevntInfoById, getEventResume, cancelAppointment, confirmAppointment } from "../../services/selectTimeService";
import {
    SELECTORS,
    MESSAGES,
    BUTTON_CONFIG,
    APPOINTMENT_STATUS,
    PARTICIPANT_TYPES,
    HTTP_STATUS,
    COLORS
} from "../../utils/appointmentConfirmedConstants";
import { toastSuccess } from './common/toast';

export async function loadAppointmentConfirmed() {
    if (!eventPayload) return;

    if (eventPayload.id && !params(EVENT_ID_PARAM)) {
        updateUrlWithEventId(eventPayload.id);
    }

    initializeStyles();
    setBusinessLogo();
    setClientPortalSignInLink();
    renderAppointmentInfo();
    handleCalendarAndDirections();
    handleConfirmCancelButtons();
}

/** Dashboard base URL (telehealth + client portal live here). From API config widgets.dashboard.url. */
function getDashboardBaseUrl() {
    return form?.data?.dashboard_base_url || form?.project?.client_portal_base_url || form?.project?.dashboard_base_url || window.location.origin;
}

/** Sign-in URL for client portal: prefer backend-provided client_portal_url, else build from dashboard base + project uuid. */
function getClientPortalUrl() {
    const fromBackend = form?.data?.client_portal_url;
    if (fromBackend) return fromBackend;
    const baseClean = String(getDashboardBaseUrl()).replace(/\/$/, '');
    const projectUuid = form?.project?.uuid;
    return projectUuid ? `${baseClean}/client/portal/${projectUuid}` : `${baseClean}/client/portal`;
}

/** Default dashboard host when API does not send dashboard_base_url (telehealth must never use assistant or embed origin). */
const DEFAULT_DASHBOARD_BASE = 'https://dashboard.pilotpractice.com';

/** Join URL for video appointments: use dashboard host + /telehealth/{text_id} so users authenticate. Never use window.location.origin (could be assistant or embed). Fallback to room_url if no text_id. */
function getTelehealthJoinUrl() {
    if (!eventRoom) return null;
    const textId = eventRoom.text_id;
    if (textId) {
        const base = form?.data?.dashboard_base_url || form?.project?.dashboard_base_url || DEFAULT_DASHBOARD_BASE;
        const baseClean = String(base).replace(/\/$/, '');
        return `${baseClean}/telehealth/${textId}`;
    }
    return eventRoom.room_url || null;
}

function setClientPortalSignInLink() {
    const link = document.getElementById('header-sign-in-client-portal');
    if (!link) return;
    link.setAttribute('href', getClientPortalUrl());
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
}

function initializeStyles() {
    const container = document.querySelector(SELECTORS.container);
    if (!container) return;

    const informationCard = document.querySelector(SELECTORS.informationCard);
    if (informationCard) informationCard.style.backgroundColor = 'transparent';

}

function setBusinessLogo() {
    if (form.project.business_photo) {
        document.querySelector('.appointment__logo')?.setAttribute('src', form.project.business_photo);
    }
}

function renderAppointmentInfo() {
    const startTime = parseInt(eventPayload.start_time);
    const staffName = getStaffName();

    const staffNameDom = document.querySelector(SELECTORS.staffInfo);
    const timeInfoDom = document.querySelector(SELECTORS.timeInfo);
    const dateInfoDom = document.querySelector(SELECTORS.dateInfo);

    if (staffNameDom) staffNameDom.textContent = staffName;
    if (timeInfoDom) timeInfoDom.textContent = moment(startTime * 1000).format('hh:mm A');

    /* Date is shown in the title only; hide the redundant date line to avoid showing the date twice. */
    if (dateInfoDom) {
        dateInfoDom.textContent = '';
        dateInfoDom.style.display = 'none';
    }

    updateAppointmentTitle();
    renderAppointmentStatus();
    renderServicesRow();
}

function getStatusLabel(status) {
    switch (status) {
        case APPOINTMENT_STATUS.CONFIRMED:
            return t('confirmed');
        case APPOINTMENT_STATUS.REQUEST:
            return t('pending');
        case APPOINTMENT_STATUS.COMPLETE:
            return t('complete');
        case APPOINTMENT_STATUS.CANCELLED:
            return t('cancelled');
        default:
            return status ? String(status) : '';
    }
}

function renderAppointmentStatus() {
    const statusEl = document.querySelector('.appointment-confirmed__status');
    if (!statusEl) return;
    const status = eventPayload?.status;
    const label = getStatusLabel(status);
    if (!label) {
        statusEl.textContent = '';
        statusEl.style.display = 'none';
        return;
    }
    statusEl.textContent = label;
    statusEl.style.display = 'inline-block';
    statusEl.className = 'appointment-confirmed__status appointment-confirmed__status--' + (status || '');
}

function renderServicesRow() {
    const procedures = eventPayload.procedures ?? appointmentSummary?.event?.procedures ?? [];
    if (!procedures.length) return;

    const informationContainer = document.querySelector('.appointment-confirmed__body .information');
    if (!informationContainer) return;

    const existingRow = informationContainer.querySelector('.appointment-services__row');
    if (existingRow) existingRow.remove();

    const names = procedures.map((p) => procedureDisplayName(p) || p.title).filter(Boolean);
    if (!names.length) return;

    const row = document.createElement('div');
    row.className = 'appointment-services__row';
    row.innerHTML = `
        <div class="appointment-services__label">${t('services')}</div>
        <div class="appointment-services__list">${names.join(', ')}</div>
    `;
    informationContainer.appendChild(row);
}

function renderAddressRow() {
    if (eventRoom !== null) return;

    const informationContainer = document.querySelector('.appointment-confirmed__body .information');
    if (!informationContainer) return;

    const existingRow = informationContainer.querySelector('.location-address__row');
    if (existingRow) existingRow.remove();

    // A remote service (video or phone) can still carry a physical
    // location_id, so the address must be replaced with a "Virtual" /
    // "Phone Call" indicator rather than shown.
    const procedures = eventPayload.procedures ?? appointmentSummary?.event?.procedures ?? [];

    if (isRemoteBooking(procedures)) {
        const allPhone = isPhoneBooking(procedures);
        const remoteRow = document.createElement('div');
        remoteRow.className = 'location-address__row';
        remoteRow.innerHTML = `
            <div class="location-address__label">
                <i data-feather="${allPhone ? 'phone' : 'video'}" class="location-address__icon"></i>
                <span class="location-address__text">${allPhone ? t('phone_call') : t('virtual')}</span>
            </div>
        `;
        informationContainer.appendChild(remoteRow);
        if (typeof feather !== 'undefined') {
            feather.replace();
        }
        return;
    }

    const locationObj = locations.find((l) => l.id === eventPayload.location_id);
    const address = locationObj?.full_address ?? eventPayload.location?.full_address ?? '';
    if (!address) return;

    const row = document.createElement('div');
    row.className = 'location-address__row';
    row.innerHTML = `
        <div class="location-address__label">
            <i data-feather="map-pin" class="location-address__icon"></i>
            <span class="location-address__text">${address}</span>
        </div>
        <button type="button" class="button--no-style location-address__copy" id="copy-address-confirmed" title="${t('copy')}" aria-label="${t('copy')}">
            <i data-feather="copy"></i>
            <span>${t('copy')}</span>
        </button>
    `;
    informationContainer.appendChild(row);
    if (typeof feather !== 'undefined') {
        feather.replace();
    }
}

function isAppointmentCancelled() {
    const hasSignature = hasValidSignature();

    if (!hasSignature) return false;

    if (eventPayload.status === APPOINTMENT_STATUS.CANCELLED) {
        return true;
    }

    if (eventPayload.status === APPOINTMENT_STATUS.CONFIRMED && eventPayload.participants && eventPayload.participants.length > 0) {
        const leadParticipant = eventPayload.participants.find(p => p.participable_type === PARTICIPANT_TYPES.LEAD);
        if (leadParticipant && leadParticipant.manually_confirmed === false) {
            return true;
        }
    }

    return false;
}

function updateAppointmentTitle() {
    const hasSignature = hasValidSignature();
    const titleContainer = document.querySelector(SELECTORS.title);
    const titleTextEl = titleContainer?.querySelector('.title-text');

    if (!titleTextEl) return;
    if (hasSignature && isAppointmentCancelled()) {
        titleTextEl.textContent = MESSAGES.ui.appointmentCancelled;
        if (titleContainer) titleContainer.style.color = COLORS.error;
    } else {
        const startTime = parseInt(eventPayload?.start_time, 10);
        const appointmentDate = startTime
            ? moment(startTime * 1000).format('dddd, MMM D, YYYY')
            : '';
        titleTextEl.textContent = appointmentDate || t('appointment_booked');
        if (titleContainer) titleContainer.style.color = '';
    }
}

function getStaffName() {
    if (eventPayload.participants && eventPayload.participants.length > 0 && params('event_id')) {
        const participant = eventPayload.participants.find(i => i.participable_type === 'App\\Models\\Staff');
        return participant ? `${participant.participable.first_name} ${participant.participable.last_name}` : '';
    }
    return getProviderFullName();
}

function handleCalendarAndDirections() {
    if (eventRoom === null) {
        handleEventRoomNullUI();
        renderAddressRow();
    } else {
        handleEventRoomUI();
        addJoinVideoButton();
    }

    addRescheduleCancelButtons();
}

function addJoinVideoButton() {
    if ((form?.appointment_channel?.show_video_call_button ?? true) === false) return;
    const joinUrl = getTelehealthJoinUrl();
    if (!joinUrl) return;

    const actionDiv = document.querySelector(SELECTORS.actionDiv);
    if (!actionDiv) return;

    const existing = actionDiv.querySelector('.appointment-join-video');
    if (existing) existing.remove();

    const link = document.createElement('a');
    link.id = 'join-video-call-link';
    link.className = 'appointment-join-video appointment-action-btn appointment-action-btn--video';
    link.href = joinUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = t('join_video_call');

    actionDiv.insertBefore(link, actionDiv.firstChild);
}

function addRescheduleCancelButtons() {
    if (isAppointmentCancelled()) return;
    if (eventPayload.status === APPOINTMENT_STATUS.CANCELLED) return;

    const actionDiv = document.querySelector(SELECTORS.actionDiv);
    if (!actionDiv) return;

    const existing = actionDiv.querySelector('.appointment-reschedule-cancel-buttons');
    if (existing) existing.remove();

    const wrapper = document.createElement('div');
    wrapper.className = 'appointment-reschedule-cancel-buttons';

    const rescheduleBtn = document.createElement('button');
    rescheduleBtn.type = 'button';
    rescheduleBtn.className = 'button--no-style appointment-action-btn appointment-action-btn--reschedule';
    rescheduleBtn.textContent = t('reschedule');
    rescheduleBtn.addEventListener('click', async () => {
        rescheduleBtn.disabled = true;
        try {
            await runRescheduleFlow(eventPayload);
        } finally {
            rescheduleBtn.disabled = false;
        }
    });

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'button--no-style appointment-action-btn appointment-action-btn--cancel';
    cancelBtn.textContent = t('cancel_appointment');
    cancelBtn.addEventListener('click', () => {
        loadAppointmentCancel();
        appointmentCancelListeners();
        document.querySelector('.appointment-cancel__container')?.style?.setProperty('transition', 'none');
        navigate(SCREEN.APPOINTMENT_CANCEL);
    });

    wrapper.appendChild(rescheduleBtn);
    wrapper.appendChild(cancelBtn);
    actionDiv.appendChild(wrapper);

    if (typeof feather !== 'undefined') {
        feather.replace();
    }
}

function handleConfirmCancelButtons() {
    const { shouldShow } = shouldShowConfirmButtons();
    const { shouldShowPendingBanner } = shouldShowPendingApprovalBanner();
    const { shouldShowCancelledBanner: showCancelledBanner } = shouldShowCancelledBanner();
    const actionDiv = document.querySelector(SELECTORS.actionDiv);
    const existingButtonsContainer = document.querySelector(SELECTORS.appointmentButtons);

    toggleActionButtonsVisibility(actionDiv);

    if (shouldShow) {
        if (!existingButtonsContainer) {
            createConfirmCancelButtons();
        } else {
            existingButtonsContainer.style.display = 'flex';
        }
    } else if (existingButtonsContainer) {
        existingButtonsContainer.style.display = 'none';
    }

    if (shouldShowPendingBanner) {
        createPendingApprovalBanner();
        removeCancelledBanner();
    } else {
        removePendingApprovalBanner();
    }

    if (showCancelledBanner) {
        createCancelledBanner();
        removePendingApprovalBanner();
    } else {
        removeCancelledBanner();
    }
}

function shouldShowConfirmButtons() {
    const hasSignature = hasValidSignature();

    if (!hasSignature) {
        return { shouldShow: false };
    }

    if (eventPayload.status === APPOINTMENT_STATUS.CONFIRMED) {
        const leadParticipant = eventPayload.participants && eventPayload.participants.length > 0
            ? eventPayload.participants.find(p => p.participable_type === PARTICIPANT_TYPES.LEAD)
            : null;

        if (leadParticipant && (
            leadParticipant.manually_confirmed === null ||
            leadParticipant.manually_confirmed === undefined
        )) {
            return { shouldShow: true };
        }
    }

    return { shouldShow: false };
}

function shouldShowPendingApprovalBanner() {
    const hasSignature = hasValidSignature();

    if (!hasSignature) {
        return { shouldShowPendingBanner: false };
    }

    if (eventPayload.status === APPOINTMENT_STATUS.REQUEST) {
        return { shouldShowPendingBanner: true };
    }

    return { shouldShowPendingBanner: false };
}

function shouldShowCancelledBanner() {
    const hasSignature = hasValidSignature();

    if (!hasSignature) {
        return { shouldShowCancelledBanner: false };
    }

    if (eventPayload.status === APPOINTMENT_STATUS.CANCELLED) {
        return { shouldShowCancelledBanner: true };
    }

    if (eventPayload.status === APPOINTMENT_STATUS.CONFIRMED && eventPayload.participants && eventPayload.participants.length > 0) {
        const leadParticipant = eventPayload.participants.find(p => p.participable_type === PARTICIPANT_TYPES.LEAD);
        if (leadParticipant && leadParticipant.manually_confirmed === false) {
            return { shouldShowCancelledBanner: true };
        }
    }

    return { shouldShowCancelledBanner: false };
}

function hasValidSignature() {
    const signature = params('signature');
    return signature && signature.length > 0;
}

function createPendingApprovalBanner() {
    const bodyContainer = document.querySelector(SELECTORS.body);
    const existingBanner = document.querySelector(SELECTORS.pendingBanner);

    if (existingBanner || !bodyContainer) return;

    const banner = document.createElement('div');
    banner.className = 'pending-approval-banner';
    banner.innerHTML = `
        <div class="banner-icon">
            <i data-feather="clock"></i>
        </div>
        <div class="banner-content">
            <div class="banner-title">${MESSAGES.banner.pendingApproval}</div>
            <div class="banner-description">${MESSAGES.banner.pendingApprovalDescription}</div>
        </div>
    `;

    const cardElement = document.querySelector(SELECTORS.card);
    if (cardElement) {
        cardElement.after(banner);
        if (typeof feather !== 'undefined') {
            feather.replace();
        }
    }
}

function removePendingApprovalBanner() {
    const existingBanner = document.querySelector(SELECTORS.pendingBanner);
    if (existingBanner) {
        existingBanner.remove();
    }
}

function createCancelledBanner() {
    const bodyContainer = document.querySelector(SELECTORS.body);
    const existingBanner = document.querySelector(SELECTORS.cancelledBanner);

    if (existingBanner || !bodyContainer) return;

    const banner = document.createElement('div');
    banner.className = 'cancelled-appointment-banner';
    banner.innerHTML = `
        <div class="banner-icon">
            <i data-feather="x-circle"></i>
        </div>
        <div class="banner-content">
            <div class="banner-title">${MESSAGES.banner.cancelled}</div>
            <div class="banner-description">${MESSAGES.banner.cancelledDescription}</div>
        </div>
    `;

    const cardElement = document.querySelector(SELECTORS.card);
    if (cardElement) {
        cardElement.after(banner);
        if (typeof feather !== 'undefined') {
            feather.replace();
        }
    }
}

function removeCancelledBanner() {
    const existingBanner = document.querySelector(SELECTORS.cancelledBanner);
    if (existingBanner) {
        existingBanner.remove();
    }
}

function toggleActionButtonsVisibility(actionDiv) {
    if (!actionDiv) return;

    const hasSignature = hasValidSignature();

    if (!hasSignature) {
        // Never offer Reschedule/Cancel on an already-cancelled appointment,
        // signature or not (the post-cancel redirect lands here unsigned).
        actionDiv.style.display = eventPayload.status === APPOINTMENT_STATUS.CANCELLED ? 'none' : 'flex';
        return;
    }

    const appointmentStatus = eventPayload.status;
    const leadParticipant = getLeadParticipant();
    const manuallyConfirmed = leadParticipant ? leadParticipant.manually_confirmed : null;

    const shouldDisplayActions = shouldShowActionButtons(appointmentStatus, manuallyConfirmed);

    actionDiv.style.display = shouldDisplayActions ? 'flex' : 'none';
}


function getLeadParticipant() {
    if (!eventPayload.participants || !eventPayload.participants.length) return null;
    return eventPayload.participants.find(p => p.participable_type === PARTICIPANT_TYPES.LEAD);
}

function shouldShowActionButtons(status, manuallyConfirmed) {
    if (status === APPOINTMENT_STATUS.REQUEST) {
        return false;
    }

    if (status === APPOINTMENT_STATUS.CONFIRMED && (manuallyConfirmed === null || manuallyConfirmed === undefined)) {
        return false;
    }

    if (status === APPOINTMENT_STATUS.CONFIRMED && manuallyConfirmed === true) {
        return true;
    }

    if (status === APPOINTMENT_STATUS.CONFIRMED && manuallyConfirmed === false) {
        return false;
    }

    if (status === APPOINTMENT_STATUS.CANCELLED) {
        return false;
    }

    if (status === APPOINTMENT_STATUS.COMPLETE) {
        return true;
    }

    return true;
}

function createButton(config, clickHandler) {
    const element = config.type === 'link' ? document.createElement('a') : document.createElement('button');

    element.id = config.id;
    element.className = config.className;
    element.textContent = config.text;

    if (config.type === 'button') {
        element.type = 'button';
    }

    if (clickHandler) {
        element.addEventListener('click', clickHandler);
    }

    return element;
}

function createButtonsContainer() {
    const container = document.createElement('div');
    container.className = 'appointment-buttons appointment-confirm-single';
    return container;
}

function createConfirmCancelButtons() {
    const informationCard = document.querySelector(SELECTORS.informationCard);
    if (!informationCard) return;

    const oldContainer = document.querySelector(SELECTORS.appointmentButtons);
    if (oldContainer) oldContainer.remove();

    const buttonsContainer = createButtonsContainer();

    const confirmButton = createButton(BUTTON_CONFIG.confirm, handleConfirmAppointment);
    const cancelLink = createButton(BUTTON_CONFIG.cancel, handleCancelAppointment);

    buttonsContainer.appendChild(confirmButton);
    buttonsContainer.appendChild(cancelLink);

    informationCard.appendChild(buttonsContainer);
}

async function handleConfirmAppointment() {
    const eventId = params('event_id');
    if (!eventId) {
        toastError(MESSAGES.errors.eventIdNotFound);
        return;
    }

    const confirmButton = document.querySelector(SELECTORS.confirmBtn);
    if (!confirmButton) return;

    try {
        setButtonLoading(confirmButton, BUTTON_CONFIG.confirm.loadingText);
        const response = await confirmAppointment(eventId);

        if (isSuccessResponse(response)) {
            await refreshAppointmentData(eventId);
            toastSuccess(MESSAGES.success.confirmAppointment);
            updateUIAfterConfirmation();
        } else {
            throw new Error(MESSAGES.errors.invalidResponse);
        }
    } catch (error) {
        toastError(MESSAGES.errors.confirmFailed);
    } finally {
        resetButton(confirmButton, BUTTON_CONFIG.confirm.text);
    }
}

async function handleCancelAppointment() {
    if (!confirm(MESSAGES.confirmations.cancelAppointment)) return;

    const eventId = params('event_id');
    if (!eventId) {
        toastError(MESSAGES.errors.eventIdNotFound);
        return;
    }

    const cancelButton = document.querySelector(SELECTORS.cancelLink);
    if (!cancelButton) return;

    try {
        setButtonLoading(cancelButton, BUTTON_CONFIG.cancel.loadingText);
        const response = await cancelAppointment(eventId);

        if (isSuccessResponse(response)) {
            await refreshAppointmentData(eventId);
            toastSuccess(MESSAGES.success.cancelAppointment);
            updateUIAfterCancellation();
        } else {
            throw new Error(MESSAGES.errors.invalidResponse);
        }
    } catch (error) {
        toastError(MESSAGES.errors.cancelFailed);
    } finally {
        resetButton(cancelButton, BUTTON_CONFIG.cancel.text);
    }
}

function setButtonLoading(button, text) {
    button.textContent = text;
    button.disabled = true;
}

function resetButton(button, originalText) {
    button.textContent = originalText;
    button.disabled = false;
}

function isSuccessResponse(response) {
    return response && (response.data?.success || response.status === HTTP_STATUS.OK || response.status === HTTP_STATUS.CREATED);
}

async function refreshAppointmentData(eventId) {
    // Refresh via the signature-gated resume route when the page carries a resume
    // signature (the standard "Manage appointment" / drop-off case); fall back to
    // the token-scoped endpoint only for legacy in-session links.
    const isDropoffSummary = params(DROPOFF_RESUME_PARAM) === 'summary';
    const resumeSignature = params('resume_signature') || (isDropoffSummary ? params('signature') : null);
    const resumeExpires = params('resume_expires') || (isDropoffSummary ? params('expires') : null);
    const updatedEventInfo = (resumeSignature && resumeExpires)
        ? await getEventResume(eventId, resumeSignature, resumeExpires)
        : await getEvevntInfoById(eventId);
    if (updatedEventInfo?.data) {
        setEventPayload(updatedEventInfo.data);
        updateUIAfterDataRefresh();
        return updatedEventInfo.data;
    }
    return null;
}

function updateUIAfterDataRefresh() {
    const { shouldShowPendingBanner } = shouldShowPendingApprovalBanner();
    const { shouldShowCancelledBanner: showCancelledBanner } = shouldShowCancelledBanner();

    if (shouldShowPendingBanner) {
        createPendingApprovalBanner();
        removeCancelledBanner();
    } else {
        removePendingApprovalBanner();
    }

    if (showCancelledBanner) {
        createCancelledBanner();
        removePendingApprovalBanner();
    } else {
        removeCancelledBanner();
    }

    updateAppointmentTitle();

    const actionDiv = document.querySelector(SELECTORS.actionDiv);
    toggleActionButtonsVisibility(actionDiv);

    const { shouldShow } = shouldShowConfirmButtons();
    const existingButtonsContainer = document.querySelector(SELECTORS.appointmentButtons);

    if (!shouldShow && existingButtonsContainer) {
        existingButtonsContainer.style.display = 'none';
    }
}

function updateUIAfterConfirmation() {
    const buttonsContainer = document.querySelector(SELECTORS.appointmentButtons);
    if (buttonsContainer) {
        buttonsContainer.style.display = 'none';
    }
    updateUIAfterDataRefresh();
}

function updateUIAfterCancellation() {
    const buttonsContainer = document.querySelector(SELECTORS.appointmentButtons);
    if (buttonsContainer) {
        buttonsContainer.style.display = 'none';
    }

    updateAppointmentTitle();

    updateUIAfterDataRefresh();
}
